import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import DownloadRounded from "@mui/icons-material/DownloadRounded";
import EnergySavingsLeafRounded from "@mui/icons-material/EnergySavingsLeafRounded";
import EventAvailableRounded from "@mui/icons-material/EventAvailableRounded";
import ShareRounded from "@mui/icons-material/ShareRounded";
import ReportProblemRounded from "@mui/icons-material/ReportProblemRounded";
import { useEffect, useState, useMemo } from "react";
import { getInsights } from "../../../../services/timeline.service";
import { AI_SUGGESTIONS, SCHEDULE_ROWS } from "../data";
import PersonalizedRecommendations from "./PersonalizedRecommendations";
import StatusBadge from "./StatusBadge";

function StepList({ items, icon: Icon, variant = "default" }) {
  const styles = {
    urgent: "border-red-100 bg-red-50/30 text-red-900 icon-red-600",
    lifestyle: "border-emerald-100 bg-emerald-50/20 text-emerald-900 icon-emerald-600",
    default: "border-slate-100 bg-white text-slate-900 icon-blue-600"
  };
  const s = styles[variant] || styles.default;

  return (
    <ol className="mt-4 space-y-3">
      {items.map((step, idx) => (
        <li key={idx} className={`flex gap-3 rounded-xl border p-3 shadow-sm ${s}`}>
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/80 text-[11px] font-bold shadow-sm ring-1 ring-black/5">
            {idx + 1}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-2">
              {Icon ? <Icon sx={{ fontSize: 16 }} className="mt-0.5 shrink-0 opacity-80" /> : null}
              <div>
                <p className="text-[13px] font-bold leading-tight">{step.title}</p>
                <p className="mt-1 text-xs leading-relaxed opacity-80">{step.description || step.detail}</p>
              </div>
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default function AISuggestions({ onDownloadSummary, onDownloadPack, onShare }) {
  const [liveSuggestions, setLiveSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const data = await getInsights();
        // Look for the 'derived' bundle which has our suggestions
        const derived = data.find(item => item.isDerived);
        if (derived && derived.suggestions) {
          setLiveSuggestions(derived.suggestions);
        }
      } catch (e) {
        console.error("Failed to fetch live suggestions", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const suggestions = useMemo(() => {
    const raw = liveSuggestions || AI_SUGGESTIONS;

    // Normalize action_plan from LLM format to the UI's action_plan_30_days format
    if (raw.action_plan && !raw.action_plan_30_days) {
      const steps = raw.action_plan;
      // Group LLM steps into 4 week-long phases
      const chunkSize = Math.max(1, Math.ceil(steps.length / 4));
      const dayRanges = ["Day 1-7", "Day 8-15", "Day 16-23", "Day 24-30"];
      raw.action_plan_30_days = dayRanges.map((day_range, i) => {
        const chunk = steps.slice(i * chunkSize, (i + 1) * chunkSize);
        return {
          day_range,
          title: chunk[0]?.title || `Phase ${i + 1}`,
          steps: chunk.map(s => s.description || s.title)
        };
      }).filter(phase => phase.steps.length > 0);
    }

    return raw;
  }, [liveSuggestions]);

  // Map LLM recommended_tests to schedule rows format
  const scheduleRows = useMemo(() => {
    if (suggestions.recommended_tests && suggestions.recommended_tests.length > 0) {
      return suggestions.recommended_tests.map((t, i) => ({
        test: t.test,
        by: t.when,
        priority: t.priority || "Medium",
        formId: `AI-TEST-${i}`
      }));
    }
    return SCHEDULE_ROWS;
  }, [suggestions]);

  return (
    <div className="min-w-0 space-y-6">
      <section className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm md:p-8">
        <PersonalizedRecommendations suggestions={suggestions} />
      </section>

      <section className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm md:p-8">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white">
            <DescriptionRounded sx={{ fontSize: 22 }} />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight text-slate-900">More suggestions</h2>
            <p className="text-sm text-slate-500">Additional checklists and lifestyle focus areas.</p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="flex flex-col">
            <p className="flex items-center gap-2 text-sm font-bold text-red-900">
              <ReportProblemRounded sx={{ fontSize: 18 }} />
              Urgent actions
            </p>
            <StepList items={suggestions.urgent_actions} variant="urgent" />
          </div>

          <div className="flex flex-col">
            <p className="flex items-center gap-2 text-sm font-bold text-emerald-900">
              <EnergySavingsLeafRounded sx={{ fontSize: 18 }} />
              Lifestyle focus
            </p>
            <StepList items={suggestions.lifestyle_focus} icon={EnergySavingsLeafRounded} variant="lifestyle" />
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm md:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <EventAvailableRounded className="text-blue-600" sx={{ fontSize: 24 }} />
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Recommended schedule</h3>
              <p className="text-xs text-slate-500">Priorities based on risk and typical follow-up windows.</p>
            </div>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200/90">
          <table className="min-w-[720px] w-full border-collapse text-left text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm">
              <tr className="border-b border-slate-200/90 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Test</th>
                <th className="px-4 py-3">By</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3 text-right">Form</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {scheduleRows.map((row) => (
                <tr key={row.formId} className="transition-colors hover:bg-slate-50/90">
                  <td className="px-4 py-3.5 font-medium text-slate-900">{row.test}</td>
                  <td className="px-4 py-3.5 text-slate-600">{row.by}</td>
                  <td className="px-4 py-3.5">
                    <StatusBadge status={row.priority} />
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-all hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 active:scale-[0.98]"
                    >
                      <DescriptionRounded sx={{ fontSize: 16 }} />
                      Get form
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-center">
          <button
            type="button"
            onClick={onDownloadSummary}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-blue-700 active:scale-[0.98]"
          >
            <DownloadRounded fontSize="small" />
            Summary PDF
          </button>
          <button
            type="button"
            onClick={onDownloadPack}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-800 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 active:scale-[0.98]"
          >
            <DownloadRounded fontSize="small" />
            Full pack
          </button>
          <button
            type="button"
            onClick={onShare}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-800 transition-all hover:bg-white active:scale-[0.98]"
          >
            <ShareRounded fontSize="small" />
            Share with clinician
            <ArrowForwardRounded fontSize="small" className="opacity-60" />
          </button>
        </div>
      </section>
    </div>
  );
}
